import { FormControl, Stack, TextField, Typography } from "@mui/material";
import { ChangeEvent, FormEvent, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { useCreateMedicationStrengthMutation } from "src/api/services/medicationStrengthsApi";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { MatchParam } from "src/constants/matches";
import { useSentryLogger } from "src/hooks/useSentryLogger";

interface Props {
  onClose: () => void;
}

const NewStrengthForm = ({ onClose }: Props) => {
  const { medicationId } = useParams<MatchParam<"MEDICATION_ID">>();
  const [createStrength] = useCreateMedicationStrengthMutation();
  const [strengthUnit, setStrengthUnit] = useState<string>("");
  const [strengthValue, setStrengthValue] = useState<number>(0);
  const [referenceUnit, setReferenceUnit] = useState<string>("");
  const [referenceAmount, setReferenceAmount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const errLog = useSentryLogger("error");

  const handleStrengthUnit = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setStrengthUnit(e.target.value);
  }, []);

  const handleStrengthValue = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setStrengthValue(Number(e.target.value));
    },
    [],
  );

  const handleReferenceAmount = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setReferenceAmount(Number(e.target.value));
    },
    [],
  );

  const handleReferenceUnit = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setReferenceUnit(e.target.value);
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      if (medicationId == null) return;

      e.preventDefault();
      setLoading(true);

      try {
        await createStrength({
          medicationId: Number(medicationId),
          body: {
            reference_amount: referenceAmount,
            reference_unit: referenceUnit,
            strength_unit: strengthUnit,
            strength_value: strengthValue,
          },
        }).unwrap();
      } catch (error) {
        errLog("Unable to create new medication strength to medication", error);
      } finally {
        setStrengthUnit("");
        setLoading(false);
        onClose();
      }
    },
    [
      createStrength,
      errLog,
      medicationId,
      onClose,
      referenceAmount,
      referenceUnit,
      strengthUnit,
      strengthValue,
    ],
  );

  return (
    <Stack gap={2} component={"form"} onSubmit={handleSubmit}>
      <Typography variant="caption" color="text.secondary">
        Example: 55 mg per 1 tablet, or 200 mg per 5 mL
      </Typography>
      <Stack direction={"row"} gap={1}>
        <FormControl>
          <TextField
            label="Strength value"
            required
            type="number"
            value={strengthValue}
            onChange={handleStrengthValue}
          />
        </FormControl>
        <FormControl>
          <TextField
            label="Strength unit"
            required
            value={strengthUnit}
            onChange={handleStrengthUnit}
          />
        </FormControl>
      </Stack>
      <Typography textAlign={"center"} fontSize={"2rem"}>
        /
      </Typography>
      <Stack direction={"row"} gap={1}>
        <FormControl>
          <TextField
            label="Reference amount"
            required
            type="number"
            value={referenceAmount}
            onChange={handleReferenceAmount}
          />
        </FormControl>
        <FormControl>
          <TextField
            label="Reference unit"
            required
            value={referenceUnit}
            onChange={handleReferenceUnit}
          />
        </FormControl>
      </Stack>
      <LoadingButton type="submit" loading={loading}>
        Create
      </LoadingButton>
    </Stack>
  );
};

export default NewStrengthForm;
